/**
 * System, Light or Dark: three real radio buttons drawn as one segmented
 * control, so arrow keys move between them and a screen reader hears
 * "Appearance, radio group, System, 1 of 3" with nothing added by hand.
 *
 * `tone="chrome"` is for the console's sidebar, which is dark in both themes.
 */

import { useId } from 'react';
import { THEME_CHOICES, useTheme, type ThemeChoice as Choice } from '../app/theme';
import own from './ThemeChoice.module.css';

const LABELS: Record<Choice, string> = { system: 'System', light: 'Light', dark: 'Dark' };

export function ThemeChoice({ tone = 'page' }: { tone?: 'page' | 'chrome' }) {
  const [choice, setChoice] = useTheme();
  const name = useId();

  return (
    <fieldset className={`${own.group} ${tone === 'chrome' ? own.chrome : ''}`}>
      <legend className="vh">Appearance</legend>
      {THEME_CHOICES.map((value) => (
        <label key={value} className={`${own.option} ${choice === value ? own.on : ''}`}>
          <input
            type="radio"
            className={own.input}
            name={name}
            value={value}
            checked={choice === value}
            onChange={() => setChoice(value)}
          />
          <Icon kind={value} />
          <span>{LABELS[value]}</span>
        </label>
      ))}
    </fieldset>
  );
}

/** Same grid and stroke as the sidebar's icons (NavIcon): 20 units, 1.6, currentColor. */
function Icon({ kind }: { kind: Choice }) {
  return (
    <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      {kind === 'system' && (<><rect x="2.5" y="3.5" width="15" height="10" rx="1.5" /><path d="M7 17h6M10 13.5V17" /></>)}
      {kind === 'light' && (<><circle cx="10" cy="10" r="3.2" /><path d="M10 2.5v2M10 15.5v2M2.5 10h2M15.5 10h2M4.7 4.7l1.4 1.4M13.9 13.9l1.4 1.4M4.7 15.3l1.4-1.4M13.9 6.1l1.4-1.4" /></>)}
      {kind === 'dark' && <path d="M16.5 11.8A7 7 0 0 1 8.2 3.5a7 7 0 1 0 8.3 8.3Z" />}
    </svg>
  );
}
